import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil } from "lucide-react";
import { api, formatApiErrorDetail } from "../api";
import { PageHeader, Table, Btn, Badge } from "../components/common";
import { Modal, Field, Selecter } from "./crm/crmui";
import { useAutoRefresh } from "../lib/autoRefresh";

const ESTADOS = [
  { value: "disponivel", label: "Disponivel" },
  { value: "ocupado", label: "Ocupado" },
  { value: "ausente", label: "Ausente" },
];

const PLATAFORMAS_DISPONIVEIS = ["CogniEtica", "Privaxia", "CogNIS2", "CogniACT", "NIS2AI", "Cogniseg Radar"];

export default function Team() {
  const [members, setMembers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [stats, setStats] = useState({});
  const [edit, setEdit] = useState(null);
  const [showTeam, setShowTeam] = useState(false);
  const [team, setTeam] = useState({ nome: "", tipo: "suporte", responsavel_id: "", plataformas: [], membros: [] });

  const load = async () => {
    try {
      const [m, t, s] = await Promise.all([
        api.get("/team/members"), api.get("/team/teams"), api.get("/team/stats"),
      ]);
      setMembers(m.data); setTeams(t.data); setStats(s.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);
  useAutoRefresh(load); // eslint-disable-line

  const saveMember = async () => {
    try {
      await api.put(`/team/members/${edit.id}`, {
        manager_id: edit.manager_id || "", competencias: edit.competencias || [],
        capacidade_tickets: parseInt(edit.capacidade_tickets) || 0,
        capacidade_reunioes: parseInt(edit.capacidade_reunioes) || 0,
        estado_disponibilidade: edit.estado_disponibilidade || "disponivel",
        meta_mensal: parseFloat(edit.meta_mensal) || 0,
      });
      toast.success("Membro atualizado."); setEdit(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const createTeam = async () => {
    if (!team.nome.trim()) return toast.error("Indique o nome.");
    try { await api.post("/team/teams", team); toast.success("Equipa criada."); setShowTeam(false); setTeam({ nome: "", tipo: "suporte", responsavel_id: "", plataformas: [], membros: [] }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const toggleMembro = (id) => {
    setTeam((t) => ({
      ...t,
      membros: (t.membros || []).includes(id)
        ? (t.membros || []).filter((x) => x !== id)
        : [...(t.membros || []), id],
    }));
  };

  const nameOf = (uid) => members.find((m) => m.id === uid)?.nome || "-";

  const memberCols = [
    { key: "nome", label: "Nome", render: (r) => <span className="font-semibold text-navy">{r.nome}</span> },
    { key: "roles", label: "Papeis", render: (r) => (r.roles || []).join(", ") },
    { key: "competencias", label: "Plataformas", render: (r) => (
      <div className="flex flex-wrap gap-1">
        {(r.competencias || []).length === 0 && <span className="text-slate-400 text-xs">-</span>}
        {(r.competencias || []).map((c) => (
          <span key={c} className="text-[10px] font-semibold px-1.5 py-0.5 rounded border border-blue-200 bg-blue-50 text-blue-700">{c}</span>
        ))}
      </div>
    ) },
    { key: "manager_id", label: "Responsavel", render: (r) => r.manager_id ? nameOf(r.manager_id) : "-" },
    { key: "capacidade_tickets", label: "Cap. tickets", render: (r) => r.capacidade_tickets ?? "-" },
    { key: "meta_mensal", label: "Meta mensal", render: (r) => r.meta_mensal ? `${r.meta_mensal} EUR` : "-" },
    { key: "estado_disponibilidade", label: "Estado", render: (r) => <Badge value={r.estado_disponibilidade || "disponivel"} /> },
    { key: "acoes", label: "", render: (r) => <button onClick={() => setEdit({ ...r })} data-testid={`edit-member-${r.id}`} className="text-blue hover:underline flex items-center gap-1"><Pencil size={14} /> Editar</button> },
  ];

  return (
    <>
      <PageHeader overline="Equipa" title="Equipa e hierarquia" action={<Btn onClick={() => setShowTeam(true)} testid="new-team-btn"><Plus size={16} /> Nova equipa</Btn>} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="team-stats">
        {[["Membros", stats.membros], ["Equipas", stats.equipas], ["Tarefas abertas", stats.tarefas_abertas], ["Aprovacoes pendentes", stats.aprovacoes_pendentes]].map(([k, v]) => (
          <div key={k} className="border border-tint rounded-lg p-4"><div className="text-xs text-muted2 uppercase">{k}</div><div className="text-2xl font-bold text-navy mt-1">{v ?? 0}</div></div>
        ))}
      </div>

      <h2 className="text-lg font-bold text-navy mb-3">Membros</h2>
      <Table testid="members-table" columns={memberCols} rows={members} />

      <h2 className="text-lg font-bold text-navy mt-8 mb-3">Equipas</h2>
      <div className="grid gap-2 md:grid-cols-3" data-testid="teams-list">
        {teams.length === 0 && <div className="text-muted2 text-sm">Sem equipas.</div>}
        {teams.map((t) => (
          <div key={t.id} className="border border-tint rounded-lg p-3" data-testid={`team-${t.id}`}>
            <div className="font-semibold text-navy">{t.nome}</div>
            <div className="text-xs text-muted2">{t.tipo} - resp: {nameOf(t.responsavel_id)}</div>
          </div>
        ))}
      </div>

      <Modal open={!!edit} onClose={() => setEdit(null)} title={`Editar ${edit?.nome || ""}`} testid="member-modal">
        {edit && (
          <>
            <Selecter label="Responsavel hierarquico" value={edit.manager_id || ""} onChange={(v) => setEdit({ ...edit, manager_id: v })}
              options={[{ value: "", label: "- Nenhum -" }, ...members.filter((m) => m.id !== edit.id).map((m) => ({ value: m.id, label: m.nome }))]} testid="member-manager" />
            <Selecter label="Estado de disponibilidade" value={edit.estado_disponibilidade || "disponivel"} onChange={(v) => setEdit({ ...edit, estado_disponibilidade: v })} options={ESTADOS} testid="member-estado" />
            <div className="mb-3">
              <label className="block text-xs uppercase text-muted2 mb-1">Plataformas (competencias) - usado no round-robin de handoffs</label>
              <div className="border border-tint rounded-md p-2 flex flex-wrap gap-1.5" data-testid="member-competencias">
                {PLATAFORMAS_DISPONIVEIS.map((p) => {
                  const on = (edit.competencias || []).includes(p);
                  return (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setEdit({ ...edit, competencias: on
                        ? (edit.competencias || []).filter((x) => x !== p)
                        : [...(edit.competencias || []), p] })}
                      className={`text-[11px] font-semibold px-2 py-1 rounded border ${on ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-300 hover:border-blue"}`}
                      data-testid={`member-comp-${p}`}
                    >
                      {on ? "✓ " : ""}{p}
                    </button>
                  );
                })}
              </div>
              <div className="text-[11px] text-muted2 mt-1">Selecione as plataformas em que este membro tem competencia (usado para auto-atribuir handoffs).</div>
            </div>
            <Field label="Capacidade de tickets" type="number" value={edit.capacidade_tickets ?? 0} onChange={(v) => setEdit({ ...edit, capacidade_tickets: v })} testid="member-cap-tickets" />
            <Field label="Capacidade de reunioes/dia" type="number" value={edit.capacidade_reunioes ?? 0} onChange={(v) => setEdit({ ...edit, capacidade_reunioes: v })} testid="member-cap-reunioes" />
            <Field label="Meta de vendas mensal (EUR)" type="number" value={edit.meta_mensal ?? 0} onChange={(v) => setEdit({ ...edit, meta_mensal: v })} testid="member-meta" />
            <div className="flex justify-end gap-2 pt-2"><Btn variant="ghost" onClick={() => setEdit(null)}>Cancelar</Btn><Btn onClick={saveMember} testid="member-save">Guardar</Btn></div>
          </>
        )}
      </Modal>

      <Modal open={showTeam} onClose={() => setShowTeam(false)} title="Nova equipa" testid="team-modal">
        <Field label="Nome" value={team.nome} onChange={(v) => setTeam({ ...team, nome: v })} testid="team-nome" />
        <Selecter label="Tipo" value={team.tipo} onChange={(v) => setTeam({ ...team, tipo: v })} options={["comercial", "suporte", "consultoria", "desenvolvimento", "financeiro"]} testid="team-tipo" />
        <Selecter label="Responsavel" value={team.responsavel_id} onChange={(v) => setTeam({ ...team, responsavel_id: v })} options={[{ value: "", label: "- Nenhum -" }, ...members.map((m) => ({ value: m.id, label: m.nome }))]} testid="team-resp" />
        <div className="mb-3">
          <label className="block text-xs uppercase text-muted2 mb-1">Plataformas de competencia da equipa (para handoffs)</label>
          <div className="border border-tint rounded-md p-2 flex flex-wrap gap-1.5" data-testid="team-plataformas">
            {PLATAFORMAS_DISPONIVEIS.map((p) => {
              const on = (team.plataformas || []).includes(p);
              return (
                <button key={p} type="button"
                  onClick={() => setTeam({ ...team, plataformas: on
                    ? (team.plataformas || []).filter((x) => x !== p)
                    : [...(team.plataformas || []), p] })}
                  className={`text-[11px] font-semibold px-2 py-1 rounded border ${on ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-300 hover:border-blue"}`}
                  data-testid={`team-plat-${p}`}>
                  {on ? "✓ " : ""}{p}
                </button>
              );
            })}
          </div>
        </div>
        <div className="mb-3">
          <label className="block text-xs uppercase text-muted2 mb-1">Membros da equipa</label>
          <div className="border border-tint rounded-md max-h-56 overflow-y-auto p-2 space-y-1" data-testid="team-membros-list">
            {members.length === 0 && <div className="text-xs text-muted2 px-1">Sem utilizadores.</div>}
            {members.map((m) => {
              const checked = (team.membros || []).includes(m.id);
              return (
                <label key={m.id} className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer ${checked ? "bg-blue/10" : "hover:bg-slate-50"}`} data-testid={`team-membro-${m.id}`}>
                  <input type="checkbox" checked={checked} onChange={() => toggleMembro(m.id)} className="rounded" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-navy truncate">{m.nome}</div>
                    <div className="text-[11px] text-muted2 truncate">{m.email}</div>
                  </div>
                </label>
              );
            })}
          </div>
          <div className="text-[11px] text-muted2 mt-1">{(team.membros || []).length} selecionado(s)</div>
        </div>
        <div className="flex justify-end gap-2 pt-2"><Btn variant="ghost" onClick={() => setShowTeam(false)}>Cancelar</Btn><Btn onClick={createTeam} testid="team-save">Criar</Btn></div>
      </Modal>
    </>
  );
}
